import { Dispatch } from 'redux';
import { RootState, store } from '../configureStore';
import { 
    fetchCredentials, 
    createCredential as createCredentialAPI,
    revokeCredential as revokeCredentialAPI 
} from '../../api/clientCredentials';
import { 
    setLoading, 
    setError, 
    setCorrelationId,
    setCredentials, 
    addCredential,
    removeCredential
} from './reducer';
import { selectUser } from '../auth/selectors';
import { toFriendlyMessage, extractCorrelationId } from '../../utils/errorUtils';
import { getUserProfile } from '../../api/userProfile';
import { setApiEnvironment } from '../../api/apiClient';
import i18n from '../../config/i18nConfig';

// Cache to track which environments have been profile-checked this session.
const profileCheckedEnvironments = new Set<string>();

/**
 * Reset profile check cache (call when user logs out or session changes).
 */
export const resetProfileCache = () => {
    profileCheckedEnvironments.clear();
};

/**
 * Check user profile access for the given environment.
 * Should only be called on environment switch, NOT on search/pagination.
 * Returns true if user has access, false otherwise.
 */
export const checkUserProfileThunk = () => {
    return async (dispatch: Dispatch, getState: () => RootState): Promise<boolean> => {
        const state = getState().ClientCredentials;
        const userState = store.getState();
        const user = selectUser(userState as any);

        if (!user?.email) {
            dispatch(setError(i18n.t('errors.unableToRetrieveMerchant')));
            return false;
        }

        const { environment } = state;
        setApiEnvironment(environment);

        // Already checked this environment — skip
        if (profileCheckedEnvironments.has(environment)) {
            return true;
        }

        try {
            const profile = await getUserProfile(user.email);

            if (!profile) {
                dispatch(setError(i18n.t('errors.noAccess')));
                return false;
            }

            if (environment === 'production') {
                const profileData = Array.isArray(profile) ? profile[0] : profile;
                const hasMerchant = profileData?.merchantProfiles && profileData.merchantProfiles.length > 0;
                const isProductionEnabled = profileData?.isProdEnable === true;

                if (!hasMerchant && !isProductionEnabled) {
                    dispatch(setError(i18n.t('errors.noProductionAccess')));
                    return false;
                }
            }

            profileCheckedEnvironments.add(environment);
            return true;
        } catch (error: any) {
            dispatch(setCorrelationId(extractCorrelationId(error) || null));
            dispatch(setError(toFriendlyMessage(error, 'Unable to verify profile access')));
            return false;
        }
    };
};

/**
 * Fetch credentials thunk — only fetches merchants.
 * Profile check is handled separately by checkUserProfileThunk.
 */
export const fetchCredentialsThunk = () => {
    return async (dispatch: Dispatch, getState: () => RootState) => {
        try {
            dispatch(setLoading(true));
            dispatch(setError(null));

            const state = getState().ClientCredentials;
            const userState = store.getState();
            const user = selectUser(userState as any);

            if (!user?.email) {
                dispatch(setError(i18n.t('errors.unableToRetrieveMerchant')));
                return;
            }

            const email = user.email;
            const { environment, searchQuery, cursor, pageSize } = state;

            setApiEnvironment(environment);

            const response = await fetchCredentials(
                email,
                environment,
                searchQuery,
                cursor,
                pageSize
            );
            dispatch(setCredentials({
                credentials: response.data,
                hasNextPage: response.next !== null,
                hasPrevPage: response.prev !== null
            }));
        } catch (error: any) {
            dispatch(setCorrelationId(extractCorrelationId(error) || null));
            dispatch(setError(toFriendlyMessage(error, 'Unable to load merchant data')));
        } finally {
            dispatch(setLoading(false));
        }
    };
};

/**
 * Create new credential thunk
 */
export const createCredentialThunk = () => {
    return async (dispatch: Dispatch, getState: () => RootState) => {
        try {
            dispatch(setLoading(true));
            
            const state = getState().ClientCredentials;
            const { environment } = state;
            
            const newCredential = await createCredentialAPI(environment);
            
            dispatch(addCredential(newCredential));
            dispatch(setLoading(false));
            
            // Optionally refresh the list
            dispatch(fetchCredentialsThunk() as any);
        } catch (error) {
            dispatch(setError(toFriendlyMessage(error, 'Unable to create credential')));
        }
    };
};

/**
 * Revoke credential thunk
 */
export const revokeCredentialThunk = (id: string) => {
    return async (dispatch: Dispatch) => {
        try {
            dispatch(setLoading(true));
            
            const success = await revokeCredentialAPI(id);
            
            if (success) {
                dispatch(removeCredential(id));
                dispatch(setLoading(false));
            } else {
                dispatch(setError('Unable to revoke credential. Please try again.'));
            }
        } catch (error) {
            dispatch(setError(toFriendlyMessage(error, 'Unable to revoke credential')));
        }
    };
};

/**
 * Retry fetching credentials (used after error)
 */
export const retryFetchCredentials = () => {
    return fetchCredentialsThunk();
};
